import React from "react"
import { Button, Grid, Typography } from "@material-ui/core"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import { GAEvent } from "../../../modules/GoogleAnalytics"
import useScrollImpressionLog from "../../../hooks/useScrollImpressionLog"
import usePlatform from "../../../hooks/usePlatform"
import { useTrack } from "@hackler/react-sdk"
import DemoRequestFormDialog from "../../../components/Dialog/DemoRequestFormDialog"
import { FormattedMessage } from "react-intl"
import { Locale } from "../../../locale"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "40px 20px",
      background: theme.palette.primary.main,
      [theme.breakpoints.up("md")]: {
        padding: "80px 72px",
      },
    },
    p: {
      color: "#E8EFFC",
      fontSize: 18,
      textAlign: "center",
      fontWeight: 700,
      [theme.breakpoints.up("md")]: {
        fontSize: 32,
      },
    },
    h2: {
      color: theme.palette.common.white,
      fontSize: 26,
      [theme.breakpoints.up("md")]: {
        fontSize: 48,
      },
    },
    buttonGroups: {
      marginTop: theme.spacing(3),
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(3),
      },
    },
    defaultButton: {
      textTransform: "none",
      color: theme.palette.common.white,
      border: "1px solid #FFF",
      borderRadius: 8,
      padding: "8px",
      "&:hover": {
        border: "1px solid #FFF",
      },
      [theme.breakpoints.up("md")]: {
        fontSize: "20px",
        padding: "12px 20px",
      },
    },
    primaryButton: {
      textTransform: "none",
      borderRadius: 8,
      background: theme.palette.common.white,
      color: theme.palette.primary.main,
      padding: "8px",
      [theme.breakpoints.up("md")]: {
        fontSize: "20px",
        padding: "12px 20px",
      },
    },
  })
)

interface Props {
  pageName?: string

  serviceButtonComponent(payload: { isMobile: boolean; className: string }): JSX.Element
}

export default function About({ pageName = "home", serviceButtonComponent }: Props) {
  const track = useTrack()
  const classes = useStyles()
  const { isMobile } = usePlatform()
  const { lang } = Locale.getDefault()
  const { ref } = useScrollImpressionLog({
    message: "9_0: 핵클과 함께 성장해보세요",
    eventKey: `homepage_${pageName}_about_impression`,
  })

  return (
    <div className={classes.root} ref={ref}>
      {lang === "ko" ? (
        <>
          <Typography className={classes.p}>지금 핵클과 함께 성장해보세요</Typography>
          <Typography variant={"h2"} className={classes.h2}>
            기간 제한 없는 {isMobile && <br />}스타트업 무료 플랜 출시!
          </Typography>
        </>
      ) : (
        <>
          <Typography className={classes.p}>Grow with Hackle</Typography>
          <Typography variant={"h2"} className={classes.h2}>
            Free Startup Plan {isMobile && <br />} Newly Launched
          </Typography>
        </>
      )}
      <Grid container spacing={isMobile ? 1 : 2} justify={"center"} className={classes.buttonGroups}>
        <Grid item xs={"auto"}>
          <DemoRequestFormDialog
            ButtonElement={(handleButtonClick) => (
              <Button
                color={"primary"}
                variant={"outlined"}
                fullWidth={isMobile}
                className={classes.defaultButton}
                onClick={() => {
                  GAEvent({
                    category: "main-6",
                    action: "click",
                    label: "request demo",
                  })
                  track({ key: `homepage_${pageName}_about_request_demo_click` })

                  try {
                    //@ts-ignore
                    analytics.track("homepage_request_demo_click", { area: `${pageName}_about` })
                  } catch (error) {}

                  handleButtonClick()
                }}>
                <strong>
                  <FormattedMessage id={"REQUEST_DEMO_TITLE"} />
                </strong>
              </Button>
            )}
          />
        </Grid>
        <Grid item xs={"auto"}>
          {serviceButtonComponent?.({
            isMobile,
            className: classes.primaryButton,
          })}
        </Grid>
      </Grid>
    </div>
  )
}
